import React, { useState } from 'react';
import { PrivateLayout, PV_THEME } from '../PrivateLayout';
import { TFMark } from '../../TFLogo';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';

const T = PV_THEME;

// ── Slide components ────────────────────────────────────────────────────────
function Eyebrow({ children }) {
  return (
    <div className="font-mono uppercase mb-2" style={{ fontSize: 9, letterSpacing: '0.3em', color: T.textFaint }}>
      {children}
    </div>
  );
}

function SlideTitle({ children, sub }) {
  return (
    <>
      <h2 className="font-machina font-bold uppercase mb-2" style={{ fontSize: 'clamp(20px,3vw,28px)', letterSpacing: '0.1em', color: T.text }}>
        {children}
      </h2>
      <p className="font-mono leading-[1.7] mb-8 pb-4" style={{ fontSize: 12, color: T.textMuted, borderBottom: `1px solid ${T.border}` }}>
        {sub}
      </p>
    </>
  );
}

function Cover() {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center py-8">
      <Eyebrow>Slide 01 · Cover</Eyebrow>
      <div className="inline-flex items-center justify-center gap-5 sm:gap-6 mb-6 mt-2">
        <TFMark color={T.text} size={44} />
        <span
          className="font-machina font-bold uppercase select-none"
          style={{
            fontSize: 'clamp(22px,3.4vw,34px)',
            letterSpacing: '0.85em',
            paddingLeft: '0.85em',
            color: T.text,
            lineHeight: 1,
          }}
        >
          TRUSTFRAME
        </span>
      </div>
      <p
        className="font-mono uppercase mb-3"
        style={{ fontSize: 13, letterSpacing: '0.15em', color: T.accent, textShadow: '0 0 8px rgba(245,166,35,0.4)' }}
      >
        Escrow & title infrastructure for art
      </p>
      <p className="font-mono mb-8" style={{ fontSize: 11, color: T.textMuted }}>
        Secure payment · Verified ownership · Standardised contracts
      </p>
      <div className="flex gap-3 flex-wrap justify-center">
        {['SRA Regulated', '$65B Market', '68–82% Margins'].map((b) => (
          <span
            key={b}
            className="font-mono uppercase"
            style={{ fontSize: 10, letterSpacing: '0.15em', color: T.accent, border: `1px solid ${T.accent}4d`, background: `${T.accent}0d`, padding: '8px 14px' }}
          >
            {b}
          </span>
        ))}
      </div>
    </div>
  );
}

function Problem() {
  const items = [
    { title: 'No Standard Escrow', desc: 'Primary sales lack secure payment infrastructure' },
    { title: 'Unclear Title Records', desc: 'Provenance and ownership chains unverified' },
    { title: 'Insolvency Risk', desc: 'Artists unprotected, become unsecured creditors' },
    { title: 'Buyer Exposure', desc: 'Risk of authenticity disputes and liens' },
    { title: 'Email Agreements', desc: 'No enforceable standardised contracts' },
  ];
  return (
    <div>
      <SlideTitle sub="The art market runs on trust, not infrastructure">The Problem</SlideTitle>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((p, i) => (
          <div
            key={p.title}
            className="rounded-[4px] p-4 flex items-start gap-4 transition-colors hover:border-red-500/30"
            style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}
          >
            <div
              className="w-9 h-9 flex items-center justify-center shrink-0 font-mono font-bold"
              style={{ background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.2)', color: T.red, fontSize: 11 }}
            >
              {String(i + 1).padStart(2, '0')}
            </div>
            <div>
              <h3 className="font-mono uppercase mb-1" style={{ fontSize: 12, letterSpacing: '0.1em', color: T.text }}>{p.title}</h3>
              <p className="font-sans leading-[1.6]" style={{ fontSize: 12, color: T.textMuted }}>{p.desc}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-5 rounded-[4px]" style={{ background: 'rgba(255,77,77,0.05)', border: '1px solid rgba(255,77,77,0.2)' }}>
        <p className="font-mono uppercase flex items-center gap-3" style={{ fontSize: 11, letterSpacing: '0.1em', color: T.red }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: T.red, animation: 'pvBlink 1.5s infinite' }} />
          Result: Disputes, non-payment, frozen assets, legal loss
        </p>
      </div>
    </div>
  );
}

function Solution() {
  const features = [
    { title: 'Escrow', desc: 'High-value art sales' },
    { title: 'Title Registry', desc: 'Provenance records' },
    { title: 'Legal Contracts', desc: 'Standardised agreements' },
    { title: 'COA Vault', desc: 'Certificate storage' },
    { title: 'Royalty Triggers', desc: 'Automated tracking' },
  ];
  return (
    <div>
      <SlideTitle sub="TrustFrame provides a neutral trust layer between buyer and seller">The Solution</SlideTitle>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
        {features.map((f) => (
          <div
            key={f.title}
            className="rounded-[4px] p-5 text-center transition-colors"
            style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}
          >
            <h3 className="font-mono uppercase mb-2" style={{ fontSize: 10, letterSpacing: '0.1em', color: T.text }}>{f.title}</h3>
            <p className="font-sans" style={{ fontSize: 11, color: T.textMuted }}>{f.desc}</p>
          </div>
        ))}
      </div>
      <div
        className="rounded-[4px] p-8 text-center"
        style={{ background: '#0a0a0b', borderLeft: `2px solid ${T.accent}`, borderTop: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}
      >
        <p
          className="font-mono uppercase mb-2"
          style={{ fontSize: 18, letterSpacing: '0.1em', color: T.text, textShadow: '0 0 8px rgba(255,255,255,0.2)' }}
        >
          Stripe + Land Registry + DocuSign for Art
        </p>
        <p className="font-mono" style={{ fontSize: 12, color: T.accent }}>
          Market infrastructure built from first principles
        </p>
      </div>
    </div>
  );
}

function Market() {
  return (
    <div>
      <SlideTitle sub="$65B+ global art market with no dominant escrow standard">Market Opportunity</SlideTitle>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {[
          { v: '$65B+', l: 'Global Art Market', s: 'Annual transaction volume' },
          { v: '£50K–£5M', l: 'Target Range', s: 'High-value private sales' },
          { v: 'Fastest', l: 'Growing Segment', s: 'Private sales outpacing auction' },
        ].map((m) => (
          <div
            key={m.l}
            className="rounded-[4px] p-6 text-center"
            style={{ background: '#0a0a0b', border: `1px solid ${T.accent}4d`, boxShadow: 'inset 0 0 20px rgba(245,166,35,0.05)' }}
          >
            <div className="font-mono mb-2" style={{ fontSize: 30, color: T.accent, textShadow: '0 0 8px rgba(245,166,35,0.4)' }}>{m.v}</div>
            <p className="font-mono uppercase mb-1" style={{ fontSize: 11, letterSpacing: '0.1em', color: T.text }}>{m.l}</p>
            <p className="font-sans" style={{ fontSize: 11, color: T.textMuted }}>{m.s}</p>
          </div>
        ))}
      </div>
      <div className="rounded-[4px] p-5" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
        <h3 className="font-mono uppercase mb-4 pb-2" style={{ fontSize: 12, letterSpacing: '0.15em', color: T.text, borderBottom: `1px solid ${T.border}` }}>
          Beachhead Users
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { l: 'Artists', d: 'Direct sales protection' },
            { l: 'Collectors', d: 'Verified purchases' },
            { l: 'Galleries', d: 'Compliance layer' },
            { l: 'Estates', d: 'Legacy management' },
          ].map((u) => (
            <div key={u.l} className="text-center">
              <p className="font-mono uppercase mb-1" style={{ fontSize: 11, letterSpacing: '0.05em', color: T.text }}>{u.l}</p>
              <p className="font-sans" style={{ fontSize: 11, color: T.textMuted }}>{u.d}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BusinessModel() {
  return (
    <div>
      <SlideTitle sub="High-margin transaction engine + recurring revenue">Business Model</SlideTitle>
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div className="rounded-[4px] p-5 relative" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
          <div className="absolute top-0 left-0 w-0.5 h-full" style={{ background: T.accent }} />
          <h3 className="font-mono uppercase mb-4" style={{ fontSize: 13, letterSpacing: '0.1em', color: T.text }}>Transaction Fees</h3>
          <p className="font-mono mb-1" style={{ fontSize: 26, color: T.accent }}>1.5% – 3%</p>
          <p className="font-sans mb-4" style={{ fontSize: 12, color: T.textMuted }}>Escrow fee per transaction</p>
          <div className="pt-3" style={{ borderTop: `1px solid ${T.border}` }}>
            <p className="font-mono" style={{ fontSize: 16, color: T.text }}>Minimum £2,500</p>
            <p className="font-sans" style={{ fontSize: 12, color: T.textMuted }}>Per deal floor</p>
          </div>
        </div>
        <div className="rounded-[4px] p-5 relative" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
          <div className="absolute top-0 left-0 w-0.5 h-full" style={{ background: T.blue }} />
          <h3 className="font-mono uppercase mb-4" style={{ fontSize: 13, letterSpacing: '0.1em', color: T.text }}>Recurring Revenue</h3>
          {[
            ['Artist Registry', '£250/yr'],
            ['Collector Vault', '£500/yr'],
            ['Gallery Compliance', '£3K–£10K/yr'],
          ].map(([k, v], i, arr) => (
            <div
              key={k}
              className="flex justify-between items-center py-3"
              style={{ borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : 'none' }}
            >
              <span className="font-sans" style={{ fontSize: 13, color: T.textMuted }}>{k}</span>
              <span className="font-mono" style={{ fontSize: 13, color: T.text }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Projections() {
  const years = [
    { year: 'Y1', deals: 15, gmv: '£2.25M', revenue: '£70K', margin: 65 },
    { year: 'Y2', deals: 35, gmv: '£6.3M', revenue: '£194K', margin: 68 },
    { year: 'Y3', deals: 60, gmv: '£13.2M', revenue: '£422K', margin: 72 },
    { year: 'Y4', deals: 120, gmv: '£31.2M', revenue: '£926K', margin: 78 },
    { year: 'Y5', deals: 200, gmv: '£60M', revenue: '£1.70M', margin: 82 },
  ];
  return (
    <div>
      <SlideTitle sub="High-margin growth trajectory">5-Year Projections</SlideTitle>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        {[
          ['Break-even', 'Mid-Y3'],
          ['Y5 GMV', '£60M'],
          ['Y5 Revenue', '£1.70M'],
          ['Y5 Margin', '82%'],
        ].map(([l, v]) => (
          <div key={l} className="text-center p-4 rounded-[2px]" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
            <p className="font-mono uppercase mb-2" style={{ fontSize: 9, letterSpacing: '0.1em', color: T.textMuted }}>{l}</p>
            <p className="font-mono" style={{ fontSize: 20, color: T.accent, textShadow: '0 0 6px rgba(245,166,35,0.3)' }}>{v}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-3 px-4 pb-2 font-mono uppercase" style={{ fontSize: 9, letterSpacing: '0.1em', color: T.textFaint, borderBottom: `1px solid ${T.border}` }}>
        <div>Period</div><div>Vol</div><div>GMV</div><div>Rev</div><div>Margin</div>
      </div>
      <div className="space-y-2 mt-2">
        {years.map((d) => (
          <div key={d.year} className="grid grid-cols-5 gap-3 items-center p-3 rounded-[2px]" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
            <span className="font-mono" style={{ fontSize: 14, color: T.text }}>{d.year}</span>
            <span className="font-mono" style={{ fontSize: 12, color: T.textMuted }}>{d.deals}</span>
            <span className="font-mono" style={{ fontSize: 12, color: T.text }}>{d.gmv}</span>
            <span className="font-mono" style={{ fontSize: 12, color: T.blue }}>{d.revenue}</span>
            <div>
              <div className="h-1.5 w-full overflow-hidden" style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <div className="h-full" style={{ width: `${d.margin}%`, background: T.green }} />
              </div>
              <p className="font-mono mt-1" style={{ fontSize: 9, color: T.textMuted }}>{d.margin}%</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Ask() {
  return (
    <div>
      <SlideTitle sub="Path to becoming trust infrastructure for art">Roadmap & The Ask</SlideTitle>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          { tag: 'Phase 1 · Y1', color: T.blue, title: 'Foundation', items: ['SRA partner secured', 'Manual registry', '12-20 pilots', 'Standard contracts'] },
          { tag: 'Phase 2 · Y2-3', color: T.accent, title: 'Platform', items: ['Digital launch', 'FCA integration', 'Gallery dashboard', 'Automated KYC'] },
          { tag: 'Phase 3 · Y4-5', color: T.green, title: 'Infrastructure', items: ['API rollout', 'Marketplaces', 'Art finance rails', 'FCA auth'] },
        ].map((p) => (
          <div key={p.tag} className="rounded-[4px] p-5" style={{ background: '#0a0a0b', border: `1px solid ${T.border}` }}>
            <span className="font-mono uppercase inline-block mb-4 px-2 py-1" style={{ fontSize: 9, letterSpacing: '0.15em', color: p.color, border: `1px solid ${p.color}66`, background: `${p.color}0d` }}>{p.tag}</span>
            <h3 className="font-mono uppercase mb-4" style={{ fontSize: 14, color: T.text }}>{p.title}</h3>
            <ul className="space-y-2">
              {p.items.map((i) => (
                <li key={i} className="flex items-start gap-2">
                  <span style={{ color: p.color }}>✓</span>
                  <span className="font-sans" style={{ fontSize: 11, color: T.textMuted }}>{i}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="rounded-[4px] p-8 text-center" style={{ background: T.accent, boxShadow: '0 0 20px rgba(245,166,35,0.2)' }}>
        <h3 className="font-mono uppercase mb-3" style={{ fontSize: 20, letterSpacing: '0.1em', color: '#0a0a0b' }}>Fundraising</h3>
        <p className="font-sans mb-4 max-w-2xl mx-auto" style={{ fontSize: 13, color: 'rgba(10,10,11,0.8)' }}>
          Seeking seed capital to build platform and execute pilot phase
        </p>
        <a
          href="mailto:investors@trustframe.legal"
          data-testid="investor-contact"
          className="inline-flex items-center gap-2 px-6 py-3 font-machina font-bold uppercase transition-opacity hover:opacity-90"
          style={{ background: '#0a0a0b', color: T.accent, fontSize: 11, letterSpacing: '0.3em' }}
        >
          investors@trustframe.legal
        </a>
      </div>
    </div>
  );
}

const SLIDES = [
  { title: 'COVER · TrustFrame', Component: Cover },
  { title: '02 · The Problem', Component: Problem },
  { title: '03 · The Solution', Component: Solution },
  { title: '04 · Market Opportunity', Component: Market },
  { title: '05 · Business Model', Component: BusinessModel },
  { title: '06 · 5-Year Projections', Component: Projections },
  { title: '07 · Roadmap & The Ask', Component: Ask },
];

export default function InvestorsSection() {
  const [idx, setIdx] = useState(0);
  const Current = SLIDES[idx].Component;
  return (
    <PrivateLayout eyebrowLabel="Investor Deck" testid="pv-investors">
      <section className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <span className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: '0.2em', color: T.textMuted }}>
              Slide {String(idx + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')}
            </span>
            <span className="font-mono" style={{ fontSize: 10, color: T.blue }}>{SLIDES[idx].title}</span>
          </div>
          <div className="h-1 w-full overflow-hidden" style={{ background: '#1e1e24' }}>
            <div className="h-full transition-all" style={{ width: `${((idx + 1) / SLIDES.length) * 100}%`, background: T.accent }} />
          </div>
        </div>

        {/* Slide canvas */}
        <div
          className="rounded-[6px] p-6 sm:p-10 min-h-[480px] flex flex-col justify-center"
          style={{ background: T.surface, border: `1px solid ${T.border}`, boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }}
          data-testid={`slide-${idx}`}
          key={idx}
        >
          <Current />
        </div>

        {/* Controls */}
        <div className="flex justify-between items-center mt-6 gap-3 flex-wrap">
          <button
            data-testid="slide-prev"
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            className="inline-flex items-center gap-2 px-4 py-2.5 font-mono uppercase transition-opacity disabled:opacity-30"
            style={{ fontSize: 10, letterSpacing: '0.2em', color: T.textMuted, border: `1px solid ${T.border}` }}
          >
            <CaretLeft size={12} weight="bold" /> Previous
          </button>
          <div className="flex gap-2">
            {SLIDES.map((s, i) => (
              <button
                key={s.title}
                data-testid={`slide-dot-${i}`}
                onClick={() => setIdx(i)}
                className="h-1.5 transition-all"
                style={{
                  width: i === idx ? 24 : 6,
                  background: i === idx ? T.accent : T.border,
                  boxShadow: i === idx ? '0 0 6px rgba(245,166,35,0.6)' : 'none',
                  borderRadius: 1,
                }}
              />
            ))}
          </div>
          <button
            data-testid="slide-next"
            onClick={() => setIdx(Math.min(SLIDES.length - 1, idx + 1))}
            disabled={idx === SLIDES.length - 1}
            className="inline-flex items-center gap-2 px-4 py-2.5 font-machina font-bold uppercase transition-opacity disabled:opacity-30"
            style={{ fontSize: 10, letterSpacing: '0.2em', color: '#0a0a0b', background: T.accent }}
          >
            Next <CaretRight size={12} weight="bold" />
          </button>
        </div>
      </section>
    </PrivateLayout>
  );
}
